import React from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useStatusCounts, StatusCounts } from '../../hooks/useStatusCounts'
import { useChatLayout } from '../../contexts/ChatLayoutContext'

/**
 * Slim (collapsed) version of the Chats list: a clean vertical rail showing each
 * status category with its live count. Clicking a category expands the list back
 * to that category; the chevron at top expands without changing category. The
 * expand chevron sits at the same vertical position (top-3) as the collapse
 * chevron on the full list, so the control doesn't jump when toggling.
 */
const CATEGORIES: { value: string; label: string; key: keyof StatusCounts }[] = [
  { value: 'queued', label: 'Queued', key: 'queued' },
  { value: 'open', label: 'Open', key: 'open' },
  { value: 'missed', label: 'Missed', key: 'missed' },
  { value: 'auto_closed', label: 'Auto', key: 'auto_closed' },
  { value: 'closed', label: 'Closed', key: 'closed' },
  { value: 'archived', label: 'Archived', key: 'archived' },
]

export const CollapsedChatsRail: React.FC = () => {
  const counts = useStatusCounts()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { setUserCollapsed } = useChatLayout()
  const activeStatus = searchParams.get('status') || 'queued'

  const openCategory = (status: string) => {
    const next = new URLSearchParams(searchParams)
    next.set('status', status)
    navigate(`${location.pathname}?${next.toString()}`)
    setUserCollapsed(false)
  }

  return (
    <div className="relative h-full w-full border-r border-border bg-surface flex flex-col items-center">
      {/* Expand chevron — same vertical position (top-3) as the collapse chevron */}
      <button
        onClick={() => setUserCollapsed(false)}
        title="Expand chats list"
        aria-label="Expand chats list"
        className="mt-3 mb-2 flex items-center justify-center w-8 h-8 rounded-lg text-txt-secondary hover:text-brand-600 hover:bg-surface-hover border border-border shadow-sm transition-colors"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      </button>

      <div className="w-full border-t border-border" />

      {/* Category cells with live counts */}
      <div className="flex-1 w-full overflow-y-auto py-1">
        {CATEGORIES.map((c) => {
          const count = counts[c.key] || 0
          const active = activeStatus === c.value
          return (
            <button
              key={c.value}
              onClick={() => openCategory(c.value)}
              title={`${c.label}${count ? ` — ${count}` : ''}`}
              className={`w-full flex flex-col items-center gap-0.5 py-2.5 border-l-2 transition-colors ${
                active
                  ? 'border-brand-600 bg-brand-50 dark:bg-brand-900/20'
                  : 'border-transparent hover:bg-surface-hover'
              }`}
            >
              <span
                className={`inline-flex items-center justify-center min-w-[22px] h-[20px] px-1 rounded-full text-[11px] font-semibold ${
                  active
                    ? 'bg-brand-600 text-white dark:bg-brand-500'
                    : count > 0
                    ? 'bg-surface-tertiary text-txt-primary dark:bg-white/10'
                    : 'bg-transparent text-txt-tertiary'
                }`}
              >
                {count}
              </span>
              <span
                className={`text-[9px] leading-none ${
                  active ? 'text-brand-700 dark:text-brand-300 font-semibold' : 'text-txt-tertiary'
                }`}
              >
                {c.label}
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}
